import React from 'react';
import '../styles/pages/DataAnalytics.css';
import { motion } from 'framer-motion';
import { useModal } from '../services/ModalService';

const DataAnalytics = () => {
  const { openContactModal } = useModal();

  return (
    <div className="data-page">
      {/* Hero Section */}
      <section style={{
        minHeight: "60vh",
        display: "flex", 
        alignItems: "center",
        position: "relative",
        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(167, 85, 247, 0.1) 100%)",
        paddingTop: "120px"
      }}>
        <div className="data-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: "center" }}
          >
            <div className="hero-badge">
              📈 Data Intelligence
            </div>
            
            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "Advanced <span className=\"gradient-text\">Data Analytics</span><br />Solutions" }}
            />
            
            <p className="hero-subtitle">
              Unlock the power of your data with our comprehensive analytics services. From data collection to visualization, we help you make data-driven decisions.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-primary"
                onClick={() => openContactModal(
                  'Data Analytics Services',
                  'Data Analytics Page - Get Started Today'
                )}
              >
                Get Started Today
              </button>
              <button 
                className="btn-secondary"
                onClick={() => openContactModal(
                  'Data Analytics Information',
                  'Data Analytics Page - Learn More'
                )}
              >
                Learn More
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="data-section">
        <div className="data-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="data-section-title gradient-text">Our Services</h2>
            <p className="data-section-subtitle">
              Comprehensive solutions tailored to meet your specific business needs
            </p>
            
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", 
              gap: "30px", 
              marginTop: "60px" 
            }}>
              
              <div className="data-glass-card" style={{ padding: "30px" }}>
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Data Warehousing</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Design and implement scalable data warehouses for centralized data management.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#ffffff !important" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Data Modeling</li><li style={{ marginBottom: "8px" }}>✓ ETL Processes</li><li style={{ marginBottom: "8px" }}>✓ Data Integration</li><li style={{ marginBottom: "8px" }}>✓ Performance Optimization</li>
                </ul>
              </div>
              <div className="data-glass-card" style={{ padding: "30px" }}>
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Business Intelligence</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Interactive dashboards and reports for real-time business insights and decision making.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#ffffff !important" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Dashboard Development</li><li style={{ marginBottom: "8px" }}>✓ KPI Tracking</li><li style={{ marginBottom: "8px" }}>✓ Real-time Analytics</li><li style={{ marginBottom: "8px" }}>✓ Self-service BI</li>
                </ul>
              </div>
              <div className="data-glass-card" style={{ padding: "30px" }}>
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Advanced Analytics</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Statistical analysis and predictive modeling to uncover hidden patterns and trends.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#ffffff !important" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Statistical Analysis</li><li style={{ marginBottom: "8px" }}>✓ Predictive Modeling</li><li style={{ marginBottom: "8px" }}>✓ Trend Analysis</li><li style={{ marginBottom: "8px" }}>✓ Forecasting</li>
                </ul>
              </div>
              <div className="data-glass-card" style={{ padding: "30px" }}>
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Data Visualization</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Interactive charts, graphs, and visualizations to communicate insights effectively.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#ffffff !important" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Interactive Charts</li><li style={{ marginBottom: "8px" }}>✓ Custom Visualizations</li><li style={{ marginBottom: "8px" }}>✓ Storytelling with Data</li><li style={{ marginBottom: "8px" }}>✓ Mobile Dashboards</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ 
        background: "rgba(139, 92, 246, 0.05)",
        padding: "100px 0"
      }}>
        <div className="data-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 style={{ 
              fontSize: "2.5rem",
              fontWeight: "700",
              textAlign: "center", 
              marginBottom: "60px"
            }} className="gradient-text">
              Why Choose Our Services
            </h2>
            
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
              gap: "30px" 
            }}>
              
              <div className="data-glass-card" style={{ padding: "25px", textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>💡</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Informed Decisions</h4>
                <p style={{ color: "#ffffff !important", lineHeight: "1.5" }}>Make strategic decisions based on data-driven insights</p>
              </div>
              <div className="data-glass-card" style={{ padding: "25px", textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>⏱️</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Real-time Insights</h4>
                <p style={{ color: "#ffffff !important", lineHeight: "1.5" }}>Access to real-time data and analytics for immediate action</p>
              </div>
              <div className="data-glass-card" style={{ padding: "25px", textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>📊</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Performance Tracking</h4>
                <p style={{ color: "#ffffff !important", lineHeight: "1.5" }}>Monitor KPIs and track progress towards business goals</p>
              </div>
              <div className="data-glass-card" style={{ padding: "25px", textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>🎯</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Competitive Advantage</h4>
                <p style={{ color: "#ffffff !important", lineHeight: "1.5" }}>Gain competitive edge through superior data insights</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="data-section">
        <div className="data-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="data-glass-card" style={{ 
              padding: "60px 40px", 
              textAlign: "center",
              background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(167, 85, 247, 0.1) 100%)"
            }}>
              <h3 style={{ 
                fontSize: "2rem", 
                marginBottom: "20px", 
                color: "#8B5CF6" 
              }}>
                Ready to Get Started?
              </h3>
              <p style={{ 
                fontSize: "1.1rem", 
                marginBottom: "30px", 
                color: "#ffffff !important",
                maxWidth: "500px",
                margin: "0 auto 30px"
              }}>
                Contact us today to learn how our data analytics services can benefit your business
              </p>
              <div style={{ display: "flex", gap: "20px", justifyContent: "center", flexWrap: "wrap" }}>
                <button 
                  className="btn-primary" 
                  onClick={() => openContactModal(
                    'Data Analytics Inquiry',
                    'Data Analytics Page - Contact Us'
                  )}
                >
                  Contact Us
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => openContactModal(
                    'Data Analytics Consultation',
                    'Data Analytics Page - Schedule Consultation'
                  )}
                >
                  Schedule Consultation
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DataAnalytics;
